import { useInjectReducer } from "@app/store";
import Dialog from "@components/Dialog";
import InputWrapper from "@components/Input";
import SelectWrapper from "@components/Select";
import PriceComponent from "@containers/Admin/OrderManage/Add/components/PriceComponent";
import {
  useLoadAllOfficeByTypeQuery,
  useLoadBankListQuery,
  useLoadProductsAvailableSchemePublicQuery,
  useLoadSliderSectionQuery,
  useLoadStateLocationQuery,
  useOrderMultiBuyMutation,
  useSendOtpMutation,
  useSubmitPaymentMutation,
  useUploadPaymentVerificationMutation,
  useVerifyOtpMutation,
  useRegisterHandraiserMutation,
  useVerifyHandraiserCodeMutation,
} from "@features/admin/consumerPurchaseManage/consumerPurchaseManageApiSlice";
import {
  makeSelectBankListOptions,
  makeSelectBankListOptionsNormalized,
  makeSelectOne,
  makeSelectPaymentMethodData,
  makeSelectQuery,
  makeSelectSelectedProduct,
} from "@features/admin/consumerPurchaseManage/consumerPurchaseManageSelector";
import consumerPurchaseManageSlice, {
  clearOne,
  setOneValue,
  setSelectedProductValue,
} from "@features/admin/consumerPurchaseManage/consumerPurchaseManageSlice";
import { enqueueSnackbar } from "@features/auth/authSlice";
import { Dropzone } from "@mantine/dropzone";
import { APP_NAME, IMAGE_BASE, RECAPTCHA_SITE_KEY } from "@utils/constants";
import { queryHelper } from "@utils/queryHelper";
import { useEffect, useState } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet";
import { BsPatchCheck, BsPatchExclamation } from "react-icons/bs";
import { FaCircleCheck } from "react-icons/fa6";
import { IoArrowDownCircleOutline } from "react-icons/io5";
import { RiDeleteBin5Line, RiImageAddFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import SliderSection from "./components/SliderSection";

const ConsumerPageInner = () => {
  useInjectReducer("consumerPurchaseManage", consumerPurchaseManageSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamsObject = Object.fromEntries([...searchParams.entries()]);
  const selectedProduct = useSelector(makeSelectSelectedProduct);
  const selectedPaymentMethod = useSelector(makeSelectPaymentMethodData);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const bankOptions = useSelector(makeSelectBankListOptions);
  const bankOptionsNormalized = useSelector(
    makeSelectBankListOptionsNormalized
  );

  const one = useSelector(makeSelectOne);
  const query = useSelector(makeSelectQuery);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({
    success: false,
    msg: "",
  });

  const [verificationId, setVerificationId] = useState("");
  const [otpbox, setOtpbox] = useState(false);

  const [payment, setPayment] = useState(false);
  const [startError, setStartError] = useState(false);
  const [initiatePayment, setInitiatePayment] = useState(true);
  const [otp, setOtp] = useState("");
  const [paymentImage, setPaymentImage] = useState({});
  const [paymentInitiationTime, setPaymentInitiationTime] = useState("");
  const [paymentEndTime, setPaymentEndTime] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expired, setExpired] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");

  // Pre-condition eligibility states
  const [preConditionOpen, setPreConditionOpen] = useState(false);
  const [preConditionChecks, setPreConditionChecks] = useState({});

  const PRE_CONDITIONS = [
    "Lost, stolen, or iCloud-locked iPhones will not be accepted.",
    `Final value will be confirmed via.  <a
                  href='https://www.arkostore.com/exchange-phone'
                  target='_blank'
                  rel='noopener noreferrer'
                  class='text-blue-600 cursor-pointer text-sm flex-1 inline-flex items-center justify-center lg:justify-start'
                >
                  ArkoStore
                  <svg
                    class='w-4 h-4 ms-2 rtl:rotate-[270deg]'
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='12'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <path
                      stroke='currentColor'
                      stroke-linecap='round'
                      stroke-linejoin='round'
                      stroke-width='1'
                      d='M18 14v4.833A1.166 1.166 0 0 1 16.833 20H5.167A1.167 1.167 0 0 1 4 18.833V7.167A1.166 1.166 0 0 1 5.167 6h4.618m4.447-2H20v5.768m-7.889 2.121 7.778-7.778'
                    />
                  </svg>
                </a>`,
  ];

  const allPreConditionsChecked =
    PRE_CONDITIONS.length > 0 &&
    PRE_CONDITIONS.every((_, i) => preConditionChecks[i] === true);

  const handlePreConditionToggle = (index) => {
    setPreConditionChecks((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handlePreConditionNext = () => {
    if (allPreConditionsChecked) {
      setPreConditionOpen(false);
    }
  };

  const handleOpenPreCondition = () => {
    setPreConditionOpen(true);
  };

  // Handraiser specific states
  const [handraiserFields, setHandraiserFields] = useState({
    device: "",
    model: "",
    old_device_imei: "",
    new_device: "",
    new_model: "",
  });

  // HANDRAISER_PRE_ORDER stage: code verification states
  const [handraiserPreOrderVerified, setHandraiserPreOrderVerified] =
    useState(false);
  const [handraiserPreOrderCode, setHandraiserPreOrderCode] = useState("");
  const [verifiedHandraiserId, setVerifiedHandraiserId] = useState(null);
  const [handraiserCodeError, setHandraiserCodeError] = useState("");

  const { data: sliderData } = useLoadSliderSectionQuery(
    searchParamsObject?.campaign || "samsung-carousel",
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [saveMultiBuy, { isLoading: saveLoading }] = useOrderMultiBuyMutation();
  const [registerHandraiser, { isLoading: handraiserLoading }] =
    useRegisterHandraiserMutation();
  const [verifyHandraiserCode, { isLoading: verifyCodeLoading }] =
    useVerifyHandraiserCodeMutation();
  const [sendOtp, { isLoading: sendLoading }] = useSendOtpMutation();
  const [verifyOtp, { isLoading: otpLoading }] = useVerifyOtpMutation();
  const [paymentSubmit, { isLoading: submitLoading }] =
    useSubmitPaymentMutation();

  const [uploadFile, { isLoading: fileLoading }] =
    useUploadPaymentVerificationMutation();

  const { data: multiBuyData, refetch: multiBuyRefetch } =
    useLoadProductsAvailableSchemePublicQuery(
      queryHelper({ ...searchParamsObject }),
      { refetchOnMountOrArgChange: true }
    );

  const { data: stateLocation, isLoading: stateloading } =
    useLoadStateLocationQuery({
      refetchOnMountOrArgChange: true,
    });
  const { data: retailOffice, isLoading: officeloading } =
    useLoadAllOfficeByTypeQuery({
      refetchOnMountOrArgChange: true,
    });

  const { data: bank, isLoading: bankloading } = useLoadBankListQuery();

  useEffect(() => {
    if (paymentInitiationTime && paymentEndTime) {
      const currentTime = new Date();
      const diffMs = paymentEndTime.getTime() - currentTime.getTime();
      const totalSec = Math.max(Math.floor(diffMs / 1000), 0);
      setSecondsLeft(totalSec);
      setExpired(false);
    }
  }, [paymentInitiationTime, paymentEndTime]);

  useEffect(() => {
    if (!startError) return;
    handleErrors();
  }, [startError, one]);

  useEffect(() => {
    if (!payment) return;
    if (secondsLeft <= 0) {
      setExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setSecondsLeft((sec) => {
        if (sec <= 1) {
          clearInterval(timer);
          setExpired(true);
          handlePaymentClose();
          setOpen(true);
          setMessage({
            success: false,
            msg: "Time expired. Please restart the payment process.",
          });
          return 0;
        }
        return sec - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [payment, secondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  useEffect(() => {
    if (!multiBuyData?.data) return;

    // Try to match from search params first
    if (
      searchParamsObject?.campaign &&
      searchParamsObject?.retailer &&
      searchParamsObject?.scheme
    ) {
      const matchedProduct = multiBuyData.data.find(
        (x) => x.name === searchParamsObject.scheme
      );
      if (matchedProduct) {
        dispatch(setSelectedProductValue({ value: matchedProduct }));
      }
    }
    // Auto-select if only one product is available and nothing is selected
    else if (multiBuyData.data.length === 1 && !selectedProduct) {
      dispatch(setSelectedProductValue({ value: multiBuyData.data[0] }));
    }
  }, [
    multiBuyData,
    searchParamsObject?.campaign,
    searchParamsObject?.retailer,
    searchParamsObject?.scheme,
    selectedProduct,
  ]);

  // Auto-select Gifts/Offers when only one option is available
  useEffect(() => {
    if (budOptions.length === 1 && !one?.bundle_product?.model) {
      dispatch(
        setOneValue({
          key: "model",
          value: budOptions[0].value,
          productType: "bundle_product",
        })
      );
    }
  }, [one?.main_product?.model, multiBuyData]);

  const handleProductSelect = (product) => {
    dispatch(setSelectedProductValue({ value: product }));
    const fullQuery = {
      ...searchParamsObject,
      scheme: product.name,
    };
    const search = new URLSearchParams(fullQuery).toString();

    multiBuyRefetch(queryHelper(fullQuery));

    navigate(`?${search}`);
  };
  const handleChange =
    (key, productType = null) =>
    (e) => {
      const value = e?.target?.value ?? e?.value;
      dispatch(setOneValue({ key, value, productType }));
    };

  // Handler for handraiser fields
  const handleHandraiserChange = (key) => (e) => {
    const value = e?.target?.value ?? e?.value;
    setHandraiserFields((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear error for this field
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  // HANDRAISER_PRE_ORDER: submit the verification code
  const handleVerifyHandraiserCode = async () => {
    setHandraiserCodeError("");
    if (!handraiserPreOrderCode.trim()) {
      setHandraiserCodeError("Please enter your verification code");
      return;
    }
    if (!executeRecaptcha) {
      setHandraiserCodeError("Recaptcha not ready. Please try again.");
      return;
    }
    const recaptchaToken = await executeRecaptcha("submit");
    verifyHandraiserCode({
      sms_code: handraiserPreOrderCode.trim(),
      campaign_id: campaignData?._id,
      recaptchaToken,
    })
      .unwrap()
      .then((res) => {
        if (res?.success) {
          const d = res.data;
          setVerifiedHandraiserId(d._id);
          setHandraiserPreOrderVerified(true);
          // Pre-fill form fields
          dispatch(
            setOneValue({ key: "customer_name", value: d.customer_name || "" })
          );
          dispatch(
            setOneValue({
              key: "customer_phone",
              value: d.customer_phone || "",
            })
          );
          dispatch(
            setOneValue({
              key: "customer_email",
              value: d.customer_email || "",
            })
          );
          dispatch(
            enqueueSnackbar({
              message:
                "Code verified! Form pre-filled with your registered details.",
              options: { variant: "success" },
            })
          );
        }
      })
      .catch((err) => {
        setHandraiserCodeError(
          err?.data?.msg || "Invalid or expired verification code"
        );
      });
  };

  // Get campaign data from API response
  const campaignData = multiBuyData?.msg?.campaign;
  const stage = multiBuyData?.msg?.stage;
  // Show handraiser registration form only during HANDRAISER_REGISTRATION stage.
  // During ONGOING and HANDRAISER_PRE_ORDER the schemes are displayed normally (no handraiser fields).
  const isHandraiser =
    campaignData?.is_handraiser === true && stage === "HANDRAISER_REGISTRATION";
  const handraiserDetails = campaignData?.handraiser_details || {};

  // Get available devices and models for handraiser
  const availableDevices =
    handraiserDetails?.allowed_devices?.map((d) => ({
      label: d.device,
      value: d.device,
    })) || [];

  const getModelsForSelectedDevice = () => {
    if (!handraiserFields.device) return [];
    const selectedDevice = handraiserDetails?.allowed_devices?.find(
      (d) => d.device === handraiserFields.device
    );
    return selectedDevice?.model?.map((m) => ({ label: m, value: m })) || [];
  };

  // Get available new device options for handraiser
  const availableNewDevices =
    handraiserDetails?.new_device_options?.map((d) => ({
      label: d.device,
      value: d.device,
    })) || [];

  const getModelsForSelectedNewDevice = () => {
    if (!handraiserFields.new_device) return [];
    const selectedDevice = handraiserDetails?.new_device_options?.find(
      (d) => d.device === handraiserFields.new_device
    );
    return (
      selectedDevice?.model?.map((m) => {
        const name = typeof m === "object" ? m.name : m;
        const stock = typeof m === "object" ? (m.stock ?? 0) : null;
        const outOfStock = stock !== null && stock <= 0;
        return {
          label: stock !== null ? `${name}` : name,
          value: name,
          isDisabled: outOfStock,
        };
      }) || []
    );
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
  };

  const isProductSelected =
    selectedProduct && Object.keys(selectedProduct).length > 0;

  const findMatchingVariant = (product, selectedValues) => {
    if (!product?.variant || !selectedValues) return null;

    return product.variant.find((variant) =>
      variant.variant_type.every(
        ({ inventory_type, value }) => selectedValues[inventory_type] === value
      )
    );
  };

  const showButton = () => {
    if (multiBuyData?.msg?.payment_qr) {
      return true;
    } else {
      return false;
    }
  };

  const matchedMainVariant = findMatchingVariant(
    selectedProduct?.main_product,
    one?.main_product
  );

  const matchedBundleVariant = findMatchingVariant(
    selectedProduct?.bundle_product,
    one?.bundle_product
  );

  const matchedMainVariantOffer = selectedProduct?.main_variant?.find(
    (variant) => variant.sku_of_seller === matchedMainVariant?.sku_of_seller
  );

  const matchedBundleVariantOffer = selectedProduct?.bundle_variant?.find(
    (variant) => variant.sku_of_seller === matchedBundleVariant?.sku_of_seller
  );

  const handleClose = () => {
    setOpen(false);
    if (one.file) {
      handleRemoveImage();
      setPaymentImage({});
    }
  };

  const handlePaymentClose = () => {
    setPayment(false);
    setOtpbox(false);
    setInitiatePayment(true);
    setOtp("");
    if (one.file) {
      handleRemoveImage();
      setPaymentImage({});
    }
  };
  const handleErrors = () => {
    setErrors({});
    if (!one?.customer_name) {
      setErrors({
        customer_name: "Please enter your name",
      });
    }
    if (!one?.customer_phone || one?.customer_phone.length !== 10) {
      setErrors((prev) => ({
        ...prev,
        customer_phone: "Please enter your mobile number",
      }));
    }
    if (!one?.customer_email) {
      setErrors((prev) => ({
        ...prev,
        customer_email: "Please enter your email",
      }));
    }

    // Handraiser specific validations
    if (isHandraiser) {
      if (!handraiserFields.device) {
        setErrors((prev) => ({
          ...prev,
          device: "Please select device",
        }));
      }
      if (!handraiserFields.model) {
        setErrors((prev) => ({
          ...prev,
          model: "Please select model",
        }));
      }
      if (!handraiserFields.old_device_imei) {
        setErrors((prev) => ({
          ...prev,
          old_device_imei: "Please enter IMEI number",
        }));
      }
      if (!handraiserFields.new_device) {
        setErrors((prev) => ({
          ...prev,
          new_device: "Please select new device",
        }));
      }
      if (!handraiserFields.new_model) {
        setErrors((prev) => ({
          ...prev,
          new_model: "Please select new model",
        }));
      }
    } else {
      // Regular flow validations (only if not handraiser)
      if (!matchedMainVariantOffer1?.variant_id) {
        setErrors((prev) => ({
          ...prev,
          main_product: "Please select one of the variant",
        }));
      }
      if (!one?.main_product?.model) {
        setErrors((prev) => ({
          ...prev,
          main_product: "Please select Product",
        }));
      }
      if (!matchedBundleVariantOffer1?._id) {
        setErrors((prev) => ({
          ...prev,
          bundle_product: "Please select one of the variant",
        }));
      }
      if (!one?.bundle_product?.model) {
        setErrors((prev) => ({
          ...prev,
          bundle_product: "Please select Product",
        }));
      }
    }
  };
  const handlePayment = () => {
    handleErrors();
    setStartError(true);

    // Base validations for all flows
    const baseValidation =
      one?.customer_phone &&
      one?.customer_phone.length === 10 &&
      one?.customer_name &&
      one?.customer_email;

    // Handraiser specific validations
    const handraiserValidation = isHandraiser
      ? handraiserFields.device &&
        handraiserFields.model &&
        handraiserFields.old_device_imei &&
        handraiserFields.new_device &&
        handraiserFields.new_model
      : true;

    // Regular flow validations
    const regularValidation = !isHandraiser
      ? one?.main_product?.model && one?.bundle_product?.model
      : true;

    if (baseValidation && handraiserValidation && regularValidation) {
      // setPayment(true);
      handleSubmit();
    }
    // setPayment(true);
    // handleVerify();
  };

  const handleUploadImages = async (files) => {
    dispatch(setOneValue({ key: "file", value: files[0] }));
    const formData = new FormData();
    formData.append("file", files[0]);

    try {
      const res = await uploadFile(formData).unwrap();
      if (res.success) {
        dispatch(
          enqueueSnackbar({
            message: "Payment verification image updated",
            options: { variant: "success" },
          })
        );
        setPaymentImage(res.data);
      }
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: err?.data?.msg || "Something went wrong",
          options: { variant: "warning" },
        })
      );
    }
  };

  const handleRemoveImage = () => {
    dispatch(setOneValue({ key: "file", value: null }));
  };

  const handleVerify = async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    const recaptchaToken = await executeRecaptcha("submit");

    const otpData = {
      customer_phone: one?.customer_phone,
      scheme_id: selectedProduct?._id,
      recaptchaToken: recaptchaToken,
    };

    sendOtp(otpData)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Mobile Verification Initiated",
              options: { variant: "success" },
            })
          );
          setVerificationId(res?.data?._id);
          setOtpbox(true);
          setPayment(true);
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Mobile Verification Failed.",
            options: {
              variant: "warning",
            },
          })
        );
        // setPayment(false);
        // setOpen(true);
        // setMessage({ success: err?.data?.success, msg: err?.data?.msg });
        setErrors(err?.data?.errors);
      });
  };

  const handleSendOtp = () => {
    const verifyOtpData = {
      customer_phone: one?.customer_phone,
      _id: verificationId,
      otp: otp,
    };

    verifyOtp(verifyOtpData)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Otp Verification Initiated",
              options: { variant: "success" },
            })
          );
          // setVerificationId(res?.data?._id);
          setInitiatePayment(true);
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Otp Verification Failed.",
            options: {
              variant: "warning",
            },
          })
        );
        // setPayment(false);
        // setOpen(true);
        // setMessage({ success: err?.data?.success, msg: err?.data?.msg });
        setErrors(err?.data?.errors);
      });
  };

  const handleSubmit = async () => {
    function returnValue(field_type, value) {
      let newValue = value || "";
      switch (field_type) {
        case "text":
        case "radio":
        case "checkbox":
          return newValue;
        case "file":
          return newValue;
      }
    }
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    const recaptchaToken = await executeRecaptcha("submit");

    // If handraiser campaign, call handraiser registration API instead
    if (isHandraiser) {
      const handraiserData = {
        customer_name: one?.customer_name,
        customer_email: one?.customer_email,
        customer_phone: one?.customer_phone,
        campaign_id: campaignData?._id,
        office_id: selectedPaymentMethod?._id || null,
        device: handraiserFields.device,
        model: handraiserFields.model,
        old_device_imei: handraiserFields.old_device_imei,
        new_device: handraiserFields.new_device,
        new_model: handraiserFields.new_model,
        recaptchaToken: recaptchaToken,
      };
      registerHandraiser(handraiserData)
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(
              enqueueSnackbar({
                message: res?.msg ?? "Handraiser Registration Successful",
                options: { variant: "success" },
              })
            );
            setOpen(true);
            setMessage({
              success: true,
              msg:
                res?.msg ||
                "Your handraiser registration has been submitted successfully!",
            });
            dispatch(clearOne());
            setHandraiserFields({
              device: "",
              model: "",
              old_device_imei: "",
              new_device: "",
              new_model: "",
            });
          }
        })
        .catch((err) => {
          setOpen(true);
          setMessage({ success: err?.data?.success, msg: err?.data?.msg });
          setErrors(err?.data?.errors);
        });
      return;
    }

    // Regular multi-buy order flow
    const multiBuyData = {
      customer_name: one?.customer_name,
      customer_email: one?.customer_email,
      customer_phone: one?.customer_phone,
      office_id: selectedPaymentMethod?._id,
      scheme_id: schemeSelected?._id,
      variant_id: matchedMainVariantOffer1?.variant_id,
      otp_verify_id: verificationId,
      bundle_variant_id: matchedBundleVariant1?._id,
      payment_initiated_using: one?.payment_initiated_using,
      payment_mobile_number: one?.payment_mobile_number,
      d_data: schemeSelected?.d_data?.map((ddata) => ({
        scheme_d_data_id: ddata._id,
        field_name: ddata.field_name,
        field_type: ddata.field_type,
        value: returnValue(ddata?.field_type, ddata.value),
        is_active: true,
      })),
      payment_verification_image: paymentImage,
      recaptchaToken: recaptchaToken,
      // Include handraiser registration id when in HANDRAISER_PRE_ORDER stage
      ...(verifiedHandraiserId
        ? { handraiser_registration_id: verifiedHandraiserId }
        : {}),
    };
    saveMultiBuy(multiBuyData)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Order placed successfully",
              options: { variant: "success" },
            })
          );
          handleRemoveImage();
          // When advance_payment is 0 the order is placed directly — skip payment step
          if (
            (res?.data?.advance_payment ?? 0) === 0 ||
            res?.data?.status === "ORDER"
          ) {
            setOpen(true);
            setMessage({ success: true, msg: res?.msg });
            dispatch(clearOne());
            return;
          }
          // Advance payment required — show payment dialog
          setPayment(true);
          setPaymentInitiationTime(new Date(res?.data?.process_start_date));
          setPaymentEndTime(new Date(res?.data?.process_end_date));
          setPaymentId(res?.data?._id);
          setAdvanceAmount(res?.data?.advance_payment);
        }
      })
      .catch((err) => {
        // dispatch(
        //   enqueueSnackbar({
        //     message: err?.data?.msg ?? "Multi Buy Order Failed.",
        //     options: {
        //       variant: "warning",
        //     },
        //   })
        // );
        setPayment(false);
        setOpen(true);
        setMessage({ success: err?.data?.success, msg: err?.data?.msg });
        setErrors(err?.data?.errors);
        handleRemoveImage();
      });
  };

  const handlePaymentSubmit = () => {
    const paymentData = {
      _id: paymentId,
      payment_mobile_number: one?.payment_mobile_number,
      payment_initiated_using: one?.payment_initiated_using,
      // advance_amount: advanceAmount,
      payment_verification_image: paymentImage,
    };

    paymentSubmit(paymentData)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Multi Buy Offer Success",
              options: { variant: "success" },
            })
          );
          setOpen(true);
          setPayment(false);
          setMessage({ success: true, msg: res?.msg });
          setPaymentImage({});
          dispatch(clearOne());
          // setPaymentInitiationTime(new Date(res?.data?.process_start_date));
          // setPaymentEndTime(new Date(res?.data?.process_end_date));
          // setPaymentId(res?.data?._id);
          // setAdvanceAmount(res?.data?.advance_payment);
        }
      })
      .catch((err) => {
        // dispatch(
        //   enqueueSnackbar({
        //     message: err?.data?.msg ?? "Multi Buy Order Failed.",
        //     options: {
        //       variant: "warning",
        //     },
        //   })
        // );
        setPayment(false);
        setOpen(true);
        setMessage({ success: err?.data?.success, msg: err?.data?.msg });
        setErrors(err?.data?.errors);
        handleRemoveImage();
        setPaymentImage({});
      });
  };

  // const handleSelectPayment = (name) => {
  //   dispatch(setOneValue({ key: "payment", value: name }));
  // };

  //new logic

  const phoneOptions =
    multiBuyData?.data?.reduce((acc, offer) => {
      if (!acc.some((opt) => opt.value === offer.main_product._id)) {
        acc.push({
          label: offer.main_product.name,
          value: offer.main_product._id,
        });
      }
      return acc;
    }, []) || [];

  const budOptions =
    multiBuyData?.data
      ?.filter((x) => x.main_product._id === one?.main_product?.model)
      .reduce((acc, offer) => {
        if (!acc.some((opt) => opt.value === offer.bundle_product._id)) {
          acc.push({
            label: offer.bundle_product.name,
            value: offer.bundle_product._id,
            description:
              offer.bundle_product?.description || offer?.description,
          });
        }
        return acc;
      }, []) || [];

  const currentOffer =
    multiBuyData?.data?.find(
      (offer) => offer.main_product._id === one.main_product?.model
    ) || null;

  const phoneInvTypes =
    currentOffer?.main_product.inventory_type.filter(
      (it) => it.inventory_type
    ) || [];

  const getActiveMainVariants = () => {
    return (
      currentOffer?.main_variant?.filter((variant) => variant.is_active) || []
    );
  };

  const getFilteredMainProductOptions = (inventoryType) => {
    const activeVariants = getActiveMainVariants();
    const currentSelections = one.main_product || {};

    const dependentTypes = ["color" || "Color"];
    const independentTypes = ["memory" || "Memory"];

    if (independentTypes.includes(inventoryType)) {
      const availableValues = new Set();
      activeVariants.forEach((variant) => {
        variant.variant_type.forEach((vt) => {
          if (vt.inventory_type === inventoryType) {
            availableValues.add(vt.value);
          }
        });
      });

      const originalOptions =
        currentOffer?.main_product?.inventory_type?.find(
          (it) => it.inventory_type === inventoryType
        )?.option || [];

      return originalOptions.filter((option) => availableValues.has(option));
    }

    if (dependentTypes.includes(inventoryType)) {
      const compatibleVariants = activeVariants.filter((variant) => {
        return variant.variant_type.every((vt) => {
          if (independentTypes.includes(vt.inventory_type)) {
            const selectedValue = currentSelections[vt.inventory_type];
            return !selectedValue || selectedValue === vt.value;
          }
          return true;
        });
      });

      const availableValues = new Set();
      compatibleVariants.forEach((variant) => {
        variant.variant_type.forEach((vt) => {
          if (vt.inventory_type === inventoryType) {
            availableValues.add(vt.value);
          }
        });
      });

      const originalOptions =
        currentOffer?.main_product?.inventory_type?.find(
          (it) => it.inventory_type === inventoryType
        )?.option || [];

      return originalOptions.filter((option) => availableValues.has(option));
    }
    const availableValues = new Set();
    activeVariants.forEach((variant) => {
      variant.variant_type.forEach((vt) => {
        if (vt.inventory_type === inventoryType) {
          availableValues.add(vt.value);
        }
      });
    });

    const originalOptions =
      currentOffer?.main_product?.inventory_type?.find(
        (it) => it.inventory_type === inventoryType
      )?.option || [];

    return originalOptions.filter((option) => availableValues.has(option));
  };

  const matchedPhoneVariant =
    currentOffer?.main_product.variant.find((variant) =>
      variant.variant_type.every(
        ({ inventory_type, value }) =>
          one?.main_product[inventory_type] === value
      )
    ) || null;

  const matchedMainVariantOffer1 = currentOffer?.main_variant?.find(
    (variant) => variant.sku_of_seller === matchedPhoneVariant?.sku_of_seller
  );

  const matchedPhoneOffer =
    currentOffer?.main_variant.find(
      (mv) => mv.variant_id === matchedPhoneVariant?._id
    ) || null;

  const budsOptions = currentOffer
    ? [
        {
          label: currentOffer.bundle_product.name,
          value: currentOffer.bundle_product._id,
        },
      ]
    : [];

  //  const currentOffer =
  // multiBuyData?.data?.find(
  //   (offer) => offer.main_product._id === one.main_product?.model
  // ) || null;

  const schemeSelected =
    multiBuyData?.data?.find(
      (offer) =>
        offer.main_product._id === one.main_product?.model &&
        offer.bundle_product._id === one?.bundle_product?.model
    ) || null;

  const getActiveBundleVariants = () => {
    return (
      schemeSelected?.bundle_variant?.filter((variant) => variant.is_active) ||
      []
    );
  };

  const getFilteredBundleProductOptions = (inventoryType) => {
    const activeVariants = getActiveBundleVariants();
    const currentSelections = one.bundle_product || {};

    const dependentTypes = ["Color" || "color"];
    const independentTypes = [];

    if (independentTypes.includes(inventoryType)) {
      const availableValues = new Set();
      activeVariants.forEach((variant) => {
        variant.variant_type.forEach((vt) => {
          if (vt.inventory_type === inventoryType) {
            availableValues.add(vt.value);
          }
        });
      });

      const originalOptions =
        schemeSelected?.bundle_product?.inventory_type?.find(
          (it) => it.inventory_type === inventoryType
        )?.option || [];

      return originalOptions.filter((option) => availableValues.has(option));
    }

    if (
      independentTypes.length === 0 ||
      !independentTypes.some((type) => currentSelections[type])
    ) {
      const availableValues = new Set();
      activeVariants.forEach((variant) => {
        variant.variant_type.forEach((vt) => {
          if (vt.inventory_type === inventoryType) {
            availableValues.add(vt.value);
          }
        });
      });

      const originalOptions =
        schemeSelected?.bundle_product?.inventory_type?.find(
          (it) => it.inventory_type === inventoryType
        )?.option || [];

      return originalOptions.filter((option) => availableValues.has(option));
    }

    if (dependentTypes.includes(inventoryType)) {
      const compatibleVariants = activeVariants.filter((variant) => {
        return variant.variant_type.every((vt) => {
          if (independentTypes.includes(vt.inventory_type)) {
            const selectedValue = currentSelections[vt.inventory_type];
            return !selectedValue || selectedValue === vt.value;
          }
          return true;
        });
      });

      const availableValues = new Set();
      compatibleVariants.forEach((variant) => {
        variant.variant_type.forEach((vt) => {
          if (vt.inventory_type === inventoryType) {
            availableValues.add(vt.value);
          }
        });
      });

      const originalOptions =
        schemeSelected?.bundle_product?.inventory_type?.find(
          (it) => it.inventory_type === inventoryType
        )?.option || [];

      return originalOptions.filter((option) => availableValues.has(option));
    }

    const availableValues = new Set();
    activeVariants.forEach((variant) => {
      variant.variant_type.forEach((vt) => {
        if (vt.inventory_type === inventoryType) {
          availableValues.add(vt.value);
        }
      });
    });

    const originalOptions =
      schemeSelected?.bundle_product?.inventory_type?.find(
        (it) => it.inventory_type === inventoryType
      )?.option || [];

    return originalOptions.filter((option) => availableValues.has(option));
  };

  const bundleInvTypes =
    schemeSelected?.bundle_product.inventory_type.filter(
      (it) => it.inventory_type
    ) || [];

  const matchedBundleVariant1 =
    schemeSelected?.bundle_product?.variant?.find((variant) =>
      variant?.variant_type?.every(
        ({ inventory_type, value }) =>
          one?.bundle_product?.[inventory_type] === value
      )
    ) || null;

  const matchedBundleVariantOffer1 = schemeSelected?.bundle_variant?.find(
    (variant) => variant.sku_of_seller === matchedBundleVariant1?.sku_of_seller
  );

  const matchedBundleOffer =
    schemeSelected?.bundle_variant.find(
      (bv) => bv.variant_id === matchedBundleVariant1?._id
    ) || null;

  const handleMainModelChange = (opt) => {
    const selectedModel = opt.value;
    dispatch(
      setOneValue({
        key: "model",
        value: selectedModel,
        productType: "main_product",
      })
    );
    phoneInvTypes.forEach((inv) => {
      dispatch(
        setOneValue({
          key: inv.inventory_type,
          value: null,
          productType: "main_product",
        })
      );
    });
    dispatch(
      setOneValue({ key: "model", value: null, productType: "bundle_product" })
    );
    bundleInvTypes.forEach((inv) => {
      dispatch(
        setOneValue({
          key: inv.inventory_type,
          value: null,
          productType: "bundle_product",
        })
      );
    });
  };

  const handleMainProductChange = (inventoryType) => (e) => {
    const value = e?.target?.value ?? e?.value;

    dispatch(
      setOneValue({ key: inventoryType, value, productType: "main_product" })
    );

    const dependentTypes = ["color" || "Color"];
    const independentTypes = ["memory" || "Memory"];

    if (independentTypes.includes(inventoryType)) {
      const activeVariants = getActiveMainVariants();

      const compatibleVariants = activeVariants.filter((variant) => {
        return variant.variant_type.some(
          (vt) => vt.inventory_type === inventoryType && vt.value === value
        );
      });

      dependentTypes.forEach((depType) => {
        const currentValue = one.main_product?.[depType];
        if (currentValue) {
          const isStillValid = compatibleVariants.some((variant) =>
            variant.variant_type.some(
              (vt) => vt.inventory_type === depType && vt.value === currentValue
            )
          );

          if (!isStillValid) {
            dispatch(
              setOneValue({
                key: depType,
                value: null,
                productType: "main_product",
              })
            );
          }
        }
      });
    }
  };

  const handleBundleProductChange = (inventoryType) => (e) => {
    const value = e?.target?.value ?? e?.value;

    dispatch(
      setOneValue({ key: inventoryType, value, productType: "bundle_product" })
    );

    const dependentTypes = ["color || Color"];
    const independentTypes = ["memory || Memory"];

    if (independentTypes.includes(inventoryType)) {
      const activeVariants = getActiveBundleVariants();

      const compatibleVariants = activeVariants.filter((variant) => {
        return variant.variant_type.some(
          (vt) => vt.inventory_type === inventoryType && vt.value === value
        );
      });

      dependentTypes.forEach((depType) => {
        const currentValue = one.bundle_product?.[depType];
        if (currentValue) {
          const isStillValid = compatibleVariants.some((variant) =>
            variant.variant_type.some(
              (vt) => vt.inventory_type === depType && vt.value === currentValue
            )
          );

          if (!isStillValid) {
            dispatch(
              setOneValue({
                key: depType,
                value: null,
                productType: "bundle_product",
              })
            );
          }
        }
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${
          isHandraiser && campaignData?.name
            ? `${campaignData.name} | ${APP_NAME}`
            : `Samsung Nepal | Latest Offers, Campaigns & Exclusive Deals | ${APP_NAME}`
        }`}</title>
      </Helmet>
      <SliderSection section_data={sliderData} />
      {/* HANDRAISER_PRE_ORDER: verification code gate */}
      {stage === "HANDRAISER_PRE_ORDER" && !handraiserPreOrderVerified && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
          <div className='bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm flex flex-col items-center gap-4'>
            <h2 className='text-xl font-bold text-center'>Enter OTP</h2>
            <input
              type='text'
              className='w-full border-2 border-gray-300 rounded-full px-5 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:border-gray-500'
              placeholder='XXXXXX'
              value={handraiserPreOrderCode}
              onChange={(e) => {
                setHandraiserPreOrderCode(e.target.value);
                if (handraiserCodeError) setHandraiserCodeError("");
              }}
              onKeyDown={(e) =>
                e.key === "Enter" && handleVerifyHandraiserCode()
              }
            />
            {handraiserCodeError && (
              <p className='text-red-500 text-sm'>{handraiserCodeError}</p>
            )}
            <button
              className='w-36 py-3 rounded-full border-2 border-black text-black font-semibold hover:bg-black hover:text-white transition-colors disabled:opacity-50'
              onClick={handleVerifyHandraiserCode}
              disabled={verifyCodeLoading}
            >
              {verifyCodeLoading ? "Verifying…" : "Submit"}
            </button>
          </div>
        </div>
      )}
      {/* {!isProductSelected && ( */}
      <div className='mt-0'>
        <h2 className='text-center mt-10'>
          {isHandraiser && campaignData?.name
            ? campaignData.name
            : multiBuyData?.data?.length > 0
              ? multiBuyData?.data[0]?.campaign_id?.name ||
                "Samsung Consumer Offers"
              : "No Offers Available"}
        </h2>
        {/* <h2 className='text-center mt-10'>Samsung Consumer Offers</h2> */}
        {/* {multiBuyData && multiBuyData.data && multiBuyData.data.length > 0 ? (
            <div className='grid grid-cols-1 md:grid-cols-2 container mx-auto mt-10 gap-12'>
              {multiBuyData?.data.map((product, index) => (
                <ProductCard
                  key={product._id || index}
                  product={product}
                  onClick={() => handleProductSelect(product)}
                  searchParamsObject={searchParamsObject}
                  multiBuyRefetch={multiBuyRefetch}
                  showButton={showButton}
                />
              ))}
            </div>
          ) : (
            <div className='block text-center text-2xl mt-10'>
              No Multi Buy Offers!!
            </div>
          )} */}
      </div>
      {/* )} */}
      {/* {isProductSelected && ( */}
      {/* <div className='mt-0'> */}
      <div className='mt-4 mx-auto max-w-3xl'>
        <h5 className='text-center'>
          <span className='mr-2'>
            {/* Here description is html so utilize it using dangerouslySetInnerHTML */}
            {isHandraiser ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: campaignData?.description || "",
                }}
              />
            ) : (
              ""
            )}
          </span>
        </h5>
        <InputWrapper
          label='Name:'
          name='customer_name'
          type='text'
          placeholder={"Enter name"}
          labelClassName='lg:min-w-[170px] text-right'
          value={one?.customer_name ?? ""}
          onChange={handleChange("customer_name")}
          disabled={stage == "HANDRAISER_PRE_ORDER"} // Make name field read-only for handraisers
          divclassname={"w-full gap-4"}
          error={errors?.customer_name}
          inputType='l-i'
        />
        <InputWrapper
          label='Mobile No.:'
          name='customer_phone'
          type='number'
          placeholder={"Enter Mobile Number"}
          value={one?.customer_phone ?? ""}
          onChange={handleChange("customer_phone")}
          disabled={stage == "HANDRAISER_PRE_ORDER"} // Make mobile number field read-only for handraisers
          divclassname={"w-full  gap-4"}
          error={errors?.customer_phone}
          labelClassName='lg:min-w-[170px] text-right'
          inputType='l-i'
        />
        <InputWrapper
          label='E-mail:'
          name='customer_email'
          type='text'
          placeholder={"Enter E-mail"}
          value={one?.customer_email ?? ""}
          onChange={handleChange("customer_email")}
          disabled={stage == "HANDRAISER_PRE_ORDER"} // Make email field read-only for handraisers
          divclassname={"w-full gap-4"}
          error={errors?.customer_email}
          labelClassName='lg:min-w-[170px] text-right'
          inputType='l-i'
        />

        {/* Handraiser-specific fields */}
        {isHandraiser && (
          <>
            {/* New Device Preference */}
            {availableNewDevices.length > 0 && (
              <>
                <div className='lg:flex items-center gap-4 text-sm mt-4'>
                  <p className='font-normal text-sm whitespace-nowrap lg:min-w-[170px] lg:text-right'>
                    Device Variant:
                  </p>
                  <div className='grid lg:grid-cols-2 flex-1 gap-4 items-center'>
                    <div>
                      <SelectWrapper
                        name='new_device'
                        label='Model'
                        placeholder=''
                        divclassname='w-full'
                        customDesign='grayDropdown'
                        options={availableNewDevices}
                        value={
                          availableNewDevices.find(
                            (opt) => opt.value === handraiserFields.new_device
                          ) || null
                        }
                        onChange={(e) => {
                          handleHandraiserChange("new_device")(e);
                          // Clear new_model when device changes
                          setHandraiserFields((prev) => ({
                            ...prev,
                            new_model: "",
                          }));
                        }}
                      />
                      {errors?.new_device && (
                        <p className='text-red-600 text-sm mt-1'>
                          {errors.new_device}
                        </p>
                      )}
                    </div>
                    <div>
                      <SelectWrapper
                        name='new_model'
                        label='Memory'
                        placeholder=''
                        divclassname='w-full'
                        customDesign='grayDropdown'
                        options={getModelsForSelectedNewDevice()}
                        value={
                          getModelsForSelectedNewDevice().find(
                            (opt) => opt.value === handraiserFields.new_model
                          ) || null
                        }
                        onChange={handleHandraiserChange("new_model")}
                        isDisabled={!handraiserFields.new_device}
                      />
                      {errors?.new_model && (
                        <p className='text-red-600 text-sm mt-1'>
                          {errors.new_model}
                        </p>
                      )}
                    </div>
                  </div>{" "}
                </div>{" "}
                {(true || handraiserFields.new_device) && (
                  <div className='w-full max-w-3xl mx-auto mt-8'>
                    <div className='bg-gray-100 rounded-2xl p-6'>
                      <h3 className='text-center text-lg font-semibold text-gray-800 mb-5'>
                        Benefits
                      </h3>

                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div className='bg-white rounded-xl px-4 py-5 text-center shadow-sm'>
                          <p className='text-sm font-medium text-gray-800'>
                            Memory upgrade upto 1TB
                          </p>
                          <p className='text-xs text-gray-500 mt-1'>
                            (worth upto NPR 15,000)
                          </p>
                        </div>

                        <div className='bg-white rounded-xl px-4 py-5 text-center shadow-sm'>
                          <p className='text-sm font-medium text-gray-800'>
                            Charger upto 60W
                          </p>
                          <p className='text-xs text-gray-500 mt-1'>
                            (worth upto NPR 5,000)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className='lg:flex items-center gap-4 text-sm mt-4'>
              <p className='font-normal text-sm whitespace-nowrap lg:min-w-[170px] lg:text-right'>
                Old Device:
              </p>
              <div className='grid lg:grid-cols-2 flex-1 gap-4 items-center'>
                <div>
                  <SelectWrapper
                    name='device'
                    placeholder=''
                    label='Model'
                    divclassname='w-full'
                    customDesign='grayDropdown'
                    options={availableDevices}
                    value={
                      availableDevices.find(
                        (opt) => opt.value === handraiserFields.device
                      ) || null
                    }
                    onChange={(e) => {
                      handleHandraiserChange("device")(e);
                      // Clear model when device changes
                      setHandraiserFields((prev) => ({
                        ...prev,
                        model: "",
                      }));
                    }}
                  />
                  {errors?.device && (
                    <p className='text-red-600 text-sm mt-1'>{errors.device}</p>
                  )}
                </div>
                <div>
                  <SelectWrapper
                    name='model'
                    placeholder=''
                    label='Memory'
                    divclassname='w-full'
                    customDesign='grayDropdown'
                    options={getModelsForSelectedDevice()}
                    value={
                      getModelsForSelectedDevice().find(
                        (opt) => opt.value === handraiserFields.model
                      ) || null
                    }
                    onChange={handleHandraiserChange("model")}
                    isDisabled={!handraiserFields.device}
                  />
                  {errors?.model && (
                    <p className='text-red-600 text-sm mt-1'>{errors.model}</p>
                  )}
                </div>
              </div>
            </div>
            <InputWrapper
              label='IMEI No'
              name='old_device_imei'
              type='text'
              placeholder={"Enter IMEI Number (e.g. 356938035643809)"}
              labelClassName='lg:min-w-[170px] text-right'
              value={handraiserFields.old_device_imei ?? ""}
              onChange={handleHandraiserChange("old_device_imei")}
              divclassname={"w-full gap-4"}
              error={errors?.old_device_imei}
              inputType='l-i'
            />
            <div className='lg:flex items-center gap-4 text-sm mt-4'>
              <p className='font-normal text-sm whitespace-nowrap lg:min-w-[170px] lg:text-right'>
                Pre-Condition:
              </p>
              <div className='flex flex-1 gap-3 items-center'>
                <button
                  type='button'
                  onClick={handleOpenPreCondition}
                  className={` hidden flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                    allPreConditionsChecked
                      ? "border-green-500 bg-green-50 text-green-700"
                      : "border-gray-400 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                      allPreConditionsChecked
                        ? "bg-green-500 border-green-500"
                        : "border-gray-400"
                    }`}
                  >
                    {allPreConditionsChecked && (
                      <svg
                        className='w-3 h-3 text-white'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={3}
                          d='M5 13l4 4L19 7'
                        />
                      </svg>
                    )}
                  </span>
                  Check Pre-Condition Eligibility
                </button>
                <div className='flex flex-col gap-3'>
                  {PRE_CONDITIONS.map((condition, index) => (
                    <label
                      key={index}
                      className='flex items-start gap-3 cursor-pointer select-none'
                      onClick={() => handlePreConditionToggle(index)}
                    >
                      <span
                        className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                          preConditionChecks[index]
                            ? "bg-gray-800 border-gray-800"
                            : "border-gray-400 bg-white"
                        }`}
                      >
                        {preConditionChecks[index] && (
                          <svg
                            className='w-3 h-3 text-white'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={3}
                              d='M5 13l4 4L19 7'
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        className='text-sm'
                        dangerouslySetInnerHTML={{
                          __html: condition,
                        }}
                      />
                    </label>
                  ))}
                </div>
                {/* {preConditionEligible && (
                  <span className='text-green-600 text-xs font-medium'>
                    Eligible ✓
                  </span>
                )} */}
              </div>
            </div>
          </>
        )}

        {/* Regular flow - Smart Phone and Gifts/Offers (only show if not handraiser) */}
        {!isHandraiser && (
          <>
            <div className='lg:flex items-center gap-4 text-sm mt-2'>
              <p className='font-normal text-sm whitespace-nowrap lg:min-w-[170px] lg:text-right'>
                Product/Device:
              </p>
              <div className='grid lg:grid-cols-3 flex-1 gap-4 items-center'>
                <SelectWrapper
                  // label='Model'
                  name='model'
                  placeholder='Select Model'
                  labelClassName='font-normal text-xs  '
                  divclassname='w-full'
                  customDesign='grayDropdown'
                  options={phoneOptions}
                  value={
                    phoneOptions.find(
                      (opt) => opt.value === one.main_product?.model
                    ) || null
                  }
                  onChange={handleMainModelChange}
                  // error={errors?.main_product}
                />
                {/* <div className='grid grid-cols-2 gap-4'> */}
                {phoneInvTypes.map((inv, idx) => {
                  const filteredOptions = getFilteredMainProductOptions(
                    inv.inventory_type
                  );
                  return (
                    <SelectWrapper
                      key={idx}
                      // label={inv.inventory_type}
                      placeholder={`Select ${inv.inventory_type}`}
                      options={filteredOptions.map((o) => ({
                        label: o,
                        value: o,
                      }))}
                      name={inv.inventory_type}
                      labelClassName='font-normal  text-xs capitalize'
                      divclassname={"w-full"}
                      customDesign='grayDropdown'
                      value={
                        filteredOptions
                          .map((o) => ({ label: o, value: o }))
                          .find(
                            (o) =>
                              o.value === one.main_product?.[inv.inventory_type]
                          ) || null
                      }
                      onChange={handleMainProductChange(inv.inventory_type)}
                    />
                  );
                })}
                {/* </div> */}
              </div>
            </div>

            {/* {matchedPhoneVariant && ( */}
            <div className='lg:flex gap-4 items-center mt-2'>
              <p className='font-bold text-sm lg:min-w-[170px] lg:block opacity-0'>
                Product/Device:{" "}
              </p>
              <p className='text-gray-600 text-sm'>
                MRP NPR {matchedPhoneOffer?.sales_price ?? "XXX"}
              </p>
              {errors?.main_product && (
                <p className='text-red-600 text-sm'>{`(${
                  errors?.main_product ?? ""
                })`}</p>
              )}
            </div>
            {/* )} */}

            <div
              className={`lg:flex items-center gap-4 text-sm ${
                matchedPhoneOffer ? "mt-4" : "mt-4"
              }`}
            >
              <p className='font-normal text-sm whitespace-nowrap lg:min-w-[170px] lg:text-right'>
                Gifts/Offers:
              </p>
              <div className='grid lg:grid-cols-3 flex-1 gap-4 items-center'>
                <SelectWrapper
                  // label='Model'
                  name='model'
                  placeholder='Select'
                  divclassname={`w-full ${bundleInvTypes.length < 1 ? "col-span-2" : ""}`}
                  labelClassName={"font-normal text-xs"}
                  customDesign='grayDropdown'
                  options={budOptions}
                  value={
                    budOptions.find(
                      (opt) => opt.value === one.bundle_product?.model
                    ) || null
                  }
                  onChange={handleChange("model", "bundle_product")}
                />
                {/* <div className='grid grid-cols-2 gap-4'> */}
                {bundleInvTypes.map((inv, idx) => {
                  const filteredOptions = getFilteredBundleProductOptions(
                    inv.inventory_type
                  );
                  return (
                    <SelectWrapper
                      key={idx}
                      placeholder={`Select ${inv.inventory_type}`}
                      options={filteredOptions.map((o) => ({
                        label: o,
                        value: o,
                      }))}
                      name={inv.inventory_type}
                      divclassname='w-full'
                      labelClassName={"font-normal text-xs capitalize"}
                      customDesign='grayDropdown'
                      value={
                        filteredOptions
                          .map((o) => ({ label: o, value: o }))
                          .find(
                            (o) =>
                              o.value ===
                              one.bundle_product?.[inv.inventory_type]
                          ) || null
                      }
                      // onChange={handleChange(inv.inventory_type, "bundle_product")}
                      onChange={handleBundleProductChange(inv.inventory_type)}
                    />
                  );
                })}
                {/* </div> */}
              </div>
            </div>
            {/* {matchedBundleVariant1 && ( */}
            <div className='lg:flex gap-4 items-center mb-2 mt-2'>
              <p className='lg:min-w-[170px] hidden lg:block opacity-0'>
                Gifts/Offers:
              </p>
              <p className='text-gray-600 text-sm'>
                MRP NPR: {matchedBundleOffer?.sales_price ?? "XXX"}
              </p>
              {errors?.bundle_product && (
                <p className='text-red-600 text-sm'>{`(${
                  errors?.bundle_product ?? ""
                })`}</p>
              )}
            </div>
            <div
              className='lg:pl-[190px] text-xs text-gray-700 mt-1 whitespace-pre-wrap'
              dangerouslySetInnerHTML={{
                __html: (
                  (
                    budOptions.find(
                      (opt) => opt.value === one.bundle_product?.model
                    ) || budOptions[0]
                  )?.description || ""
                )
                  .replace(/\\n/g, "<br />")
                  .replace(/\n/g, "<br />"),
              }}
            />
            {/* )} */}
            {matchedMainVariantOffer1?.scheme_price != null &&
              matchedBundleVariantOffer1?.scheme_price != null && (
                <div className='text-gray-700 mt-4 mb-4 font-semibold text-center'>
                  <span className='flex items-center gap-4'>
                    {/* <p className='font-bold text-sm whitespace-nowrap opacity-0 hidden lg:block min-w-[98px] lg:min-w-[98px]'>
                  Smart Phone:{" "}
                </p> */}
                    <span className='font-normal text-black text-sm whitespace-nowrap lg:min-w-[170px] text-right'>
                      Offer Price:
                    </span>
                    <PriceComponent
                      className='mt-0'
                      price={
                        matchedMainVariantOffer1.sales_price +
                        matchedBundleVariantOffer1.sales_price
                      }
                      sales_price={
                        matchedMainVariantOffer1.scheme_price +
                        matchedBundleVariantOffer1.scheme_price
                      }
                    />
                  </span>
                </div>
              )}
            <div
              className={`${
                matchedBundleVariantOffer1 ? "mt-0" : "mt-10"
              } text-sm text-center`}
            >
              By submitting this form, you agree that your information can be
              used for marketing purpose
            </div>
          </>
        )}
        {/* <h5 className='text-sm text-center mt-4'>Payment Methods</h5> */}
        {/* <div className='flex gap-2 justify-evenly font-semibold'>
              <button
                className={`${
                  one.payment === "fonepay"
                    ? "border-2 border-black"
                    : "border-1 border-gray-200"
                }  px-3 py-2 border  rounded-md hover:border-2`}
                onClick={() => handleSelectPayment("fonepay")}
              >
                Fonepay
              </button>
              <button
                className={`${
                  one.payment === "esewa"
                    ? "border-2 border-black"
                    : "border-1 border-gray-200"
                }  px-3 py-2 border  rounded-md hover:border-2`}
                onClick={() => handleSelectPayment("esewa")}
              >
                Esewa
              </button>
              <button
                className={`${
                  one.payment === "khalti"
                    ? "border-2 border-black"
                    : "border-1 border-gray-200"
                }  px-3 py-2 border  rounded-md hover:border-2`}
                onClick={() => handleSelectPayment("khalti")}
              >
                Khalti
              </button>
            </div> */}
        {/* {selectedPaymentMethod && (
              <>
                <h5 className='text-gray-400 text-center text-sm'>
                  Scan QR to proceed Advance Payment
                </h5>
                <div className='border border-1 w-1/2 mx-auto p-4'>
                  <img
                    src={`${IMAGE_BASE}${selectedPaymentMethod.payment_qr.path}`}
                    className='m-auto w-24 h-24 object-contain'
                  />
                </div>
              </>
            )} */}
        {/* <div className='text-gray-700 flex gap-2 mt-4 mb-4 font-semibold text-center'>
          <p className='font-bold text-sm whitespace-nowrap opacity-0 hidden lg:block min-w-[98px] lg:min-w-[98px]'>
            Smart Phone:{" "}
          </p>
          <span className='font-normal text-gray-500'>Advance Amount:</span> NPR{" "}
          {currentOffer.advance_amount}
        </div> */}

        <button
          className={`rounded-md border border-1 px-3 py-1 font-semibold mt-10 block mx-auto mb-4 flex items-center justify-center gap-2 ${
            isHandraiser && !allPreConditionsChecked
              ? "opacity-40 cursor-not-allowed"
              : ""
          }`}
          onClick={handlePayment}
          disabled={saveLoading || (isHandraiser && !allPreConditionsChecked)}
        >
          Submit
          {saveLoading && (
            <span className='w-4 h-4 border-2 border-t-transparent border-gray-700 rounded-full animate-spin' />
          )}
        </button>
      </div>
      {/* </div> */}
      {/* )} */}

      {/* Pre-Condition Eligibility Dialog */}
      <Dialog
        opened={preConditionOpen}
        onClose={() => setPreConditionOpen(false)}
        size='lg'
        radius='lg'
        title={
          <h2 className='text-xl font-bold text-center w-full'>
            Handraiser offer
          </h2>
        }
      >
        <div className='px-2 pb-4'>
          <p className='font-semibold text-sm mb-3'>Pre-conditions:</p>

          <div className='mt-6 rounded-xl bg-gray-100 p-4 text-sm text-gray-600 text-center'>
            <span className='font-semibold text-gray-800'>Important Note:</span>{" "}
            The trade-in is applicable only to fully functional phones. If a
            device has any functional, hardware, or software-related issues, it
            will not qualify under the condition categories outlined below and
            will be subject to a separate detailed evaluation.
          </div>

          <button
            type='button'
            onClick={handlePreConditionNext}
            disabled={!allPreConditionsChecked}
            className={`mt-6 block mx-auto px-8 py-2 rounded-full border text-sm font-semibold transition-colors ${
              allPreConditionsChecked
                ? "border-gray-800 bg-white text-gray-800 hover:bg-gray-100 cursor-pointer"
                : "border-gray-300 text-gray-300 cursor-not-allowed"
            }`}
          >
            Next
          </button>
        </div>
      </Dialog>

      <Dialog opened={open} onClose={handleClose} size='lg' radius='lg'>
        {message?.success ? (
          <BsPatchCheck className='block mx-auto' size={60} color='green' />
        ) : (
          <BsPatchExclamation className='block mx-auto' size={60} color='red' />
        )}
        <p className='text-gray-400 font-normal text-xl text-center mt-4 px-10 mb-4'>
          {message?.msg}
        </p>
      </Dialog>

      <Dialog
        opened={payment}
        onClose={handlePaymentClose}
        title={
          <>
            Payment Verification{" "}
            {/* <span className='ml-4 font-mono'>
              {minutes}:{seconds}
            </span> */}
          </>
        }
        size='lg'
      >
        {/* {expired && (
          <p className='text-red-500 text-center mb-4'>
            Time expired. Please restart the payment process.
          </p>
        )} */}

        {true && (
          <>
            <hr className=' border-t border-gray-300' />
            <div className='flex justify-between items-center'>
              <InputWrapper
                label='Customer Mobile Number'
                name='customer_phone'
                type='number'
                labelClassName='label-style'
                value={one?.customer_phone ?? ""}
                onChange={handleChange("customer_phone")}
                divclassname={"lg:w-1/2 mb-5"}
                error={errors?.customer_phone}
                disabled
              />

              <div className='relative w-14 h-14'>
                <div className='absolute inset-0 rounded-full bg-yellow-300 animate-pulse-glow'></div>

                <div className='relative rounded-full bg-yellow-300 w-14 h-14 flex items-center justify-center font-mono'>
                  {minutes}:{seconds}
                </div>
              </div>
            </div>
          </>
        )}
        {false && (
          <>
            <div className='p-2'>
              <h5 className='text-gray-400'>
                Verify Mobile Number Before Payment
              </h5>
              <hr className='my-2 border-t border-gray-300' />
              <div className='lg:flex gap-2 items-center'>
                <InputWrapper
                  label='Customer Mobile Number'
                  name='customer_phone'
                  type='number'
                  value={one?.customer_phone ?? ""}
                  onChange={handleChange("customer_phone")}
                  divclassname={"lg:w-1/3 mb-5"}
                  error={errors.customer_phone}
                  disabled
                />
                {!initiatePayment && !otpbox && (
                  <button
                    className='rounded-md border border-1 px-4 py-1 font-semibold h-9'
                    onClick={handleVerify}
                    disabled={initiatePayment ? true : false}
                  >
                    {sendLoading ? "Sending..." : "Send OTP"}
                  </button>
                )}
                {otpbox && !initiatePayment && (
                  <>
                    <InputWrapper
                      label='OTP Number'
                      name='otp'
                      type='number'
                      value={otp ?? ""}
                      onChange={handleOtpChange}
                      divclassname={"lg:w-1/3 mb-5"}
                      // error={errors.otp_number}
                    />

                    <button
                      className={`rounded-md border border-1 px-4 py-1 font-semibold h-9`}
                      onClick={handleSendOtp}
                      disabled={initiatePayment ? true : false}
                    >
                      {otpLoading ? "Verifying..." : "Verify"}
                    </button>
                  </>
                )}
                {initiatePayment && (
                  <span className='text-green-500 font-semibold flex items-center gap-1'>
                    <FaCircleCheck />
                    Verified
                  </span>
                )}
              </div>
            </div>

            <hr className='my-1 border-t border-gray-300' />
          </>
        )}
        {initiatePayment && (
          <div
            className={`transition-all duration-300${
              initiatePayment ? "" : "pointer-events-none opacity-50"
            }`}
          >
            {false && (
              <div className='grid lg:grid-cols-2 gap-4 '>
                <div className='border-r-2 border-gray-300 text-center '>
                  {selectedPaymentMethod && (
                    <>
                      <h5 className=' text-sm mt-1.5'>Scan QR to Pay</h5>
                      <div className='m-2 text-center  rounded-md shadow-sm border border-gray-300'>
                        <img
                          src={`${IMAGE_BASE}${selectedPaymentMethod?.payment_qr?.path}`}
                          className='m-auto object-contain  w-2/3'
                        />
                        {/* create download button for image */}
                      </div>
                      <a
                        href={`${IMAGE_BASE}${selectedPaymentMethod?.payment_qr?.path}`}
                        target='_blank'
                        download='qrcode.png'
                        className='mt-2 inline-block bg-gray-500 text-white px-4 py-2 rounded-md  label-style'
                      >
                        <IoArrowDownCircleOutline className='inline-block mr-2 text-lg' />
                        Download Now
                      </a>
                    </>
                  )}
                </div>

                <div className='mt-2'>
                  <div className=' text-sm mb-4 font-bold label-style'>
                    Upload Payment Proof
                  </div>
                  <div>
                    <SelectWrapper
                      label='Payment Method'
                      name='payment_initiated_using'
                      placeholder='Select Payment Method'
                      divclassname='w-full'
                      labelClassName='label-style text-sm'
                      options={bankOptions}
                      value={
                        bankOptionsNormalized[
                          one?.payment_initiated_using ?? ""
                        ]
                      }
                      onChange={handleChange("payment_initiated_using")}
                      error={errors?.payment_initiated_using}
                    />
                    <InputWrapper
                      label='Payment Mobile Number'
                      name='payment_mobile_number'
                      type='number'
                      value={one?.payment_mobile_number ?? ""}
                      onChange={handleChange("payment_mobile_number")}
                      divclassname={"w-full my-4"}
                      disabled={!initiatePayment}
                      // withAsterisk={true}
                      labelClassName='label-style text-sm'
                      error={errors?.payment_mobile_number}
                    />
                    {/* 
                    <InputWrapper
                      label='Payment Initiation (eg. Esewa, NMB Bank, etc.)'
                      name='payment_initiated_using'
                      type='text'
                      value={one?.payment_initiated_using ?? ""}
                      onChange={handleChange("payment_initiated_using")}
                      divclassname={"w-full mb-4"}
                      disabled={!initiatePayment}
                      withAsterisk={true}
                      error={errors.payment_initiated_using}
                    /> */}
                    <>
                      {" "}
                      <Dropzone onDrop={handleUploadImages}>
                        <div className='text-center flex items-center justify-center gap-2  py-5 rounded-md'>
                          <RiImageAddFill className='text-lg text-primary' />
                          <span className='text-primary'>
                            Upload Payment Proof
                          </span>
                        </div>
                      </Dropzone>
                      <div className='text-sm'>
                        Accepted formats: JPG, PNG, JPEG, or WebP
                      </div>
                      <div className='mt-2 grid grid-cols-4 gap-4'>
                        {" "}
                        {one.file && (
                          <div className='relative p-2 border border-dashed mb-2'>
                            <span
                              className='text-white p-2 bg-primary shadow-md rounded-full absolute -top-2 -right-2 z-10 cursor-pointer'
                              onClick={() => handleRemoveImage()}
                            >
                              <RiDeleteBin5Line />
                            </span>
                            <img
                              src={URL.createObjectURL(one.file)}
                              alt='preview'
                              title='review-img'
                              className='max-h-40 object-contain'
                            />
                          </div>
                        )}
                      </div>
                      {errors?.payment_verification_image && (
                        <p className='text-red-500 text-xs'>
                          Upload File First
                        </p>
                      )}
                    </>
                    <InputWrapper
                      label='Advance Amount'
                      name='advance_amount'
                      type='number'
                      value={advanceAmount}
                      labelClassName='label-style text-sm'
                      divclassname={"w-full mb-4"}
                      disabled
                    />
                  </div>
                </div>
              </div>
            )}
            <div className='flex mt-4 gap-y-1 justify-end border-t border-gray-300 pt-2'>
              <button
                className='rounded-md border border-1 px-3 py-1 font-semibold mb-4 bg-gray-500 text-white mr-2  label-style'
                onClick={handlePaymentClose}
              >
                Close
              </button>
              <button
                className='rounded-md border border-1 px-3 py-1 font-semibold mb-4  label-style'
                onClick={handlePaymentSubmit}
                disabled={!initiatePayment || !paymentImage}
              >
                {saveLoading ? "Submitting..." : "Submit for Verification"}
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

const ConsumerPage = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <ConsumerPageInner />
    </GoogleReCaptchaProvider>
  );
};

export default ConsumerPage;
